import { useEffect, useState, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import axios from "axios";
import { AnimatePresence } from "framer-motion";

import StarCanvas from "@/components/StarCanvas";
import NavHeader from "@/components/NavHeader";
import HeroSection from "@/components/HeroSection";
import EditorialMarquee from "@/components/EditorialMarquee";
import StoryGenerator from "@/components/StoryGenerator";
import VoicePlayer from "@/components/VoicePlayer";
import ManifestoSection from "@/components/ManifestoSection";
import PricingSection from "@/components/PricingSection";
import TestimonialsSection from "@/components/TestimonialsSection";
import Footer from "@/components/Footer";
import AuthModal from "@/components/AuthModal";
import MyStories from "@/pages/MyStories";
import StoryReader from "@/pages/StoryReader";
import SharedStory from "@/pages/SharedStory";
import VerifyEmail from "@/pages/VerifyEmail";
import PlaylistPlayer from "@/pages/PlaylistPlayer";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import { Toaster } from "@/components/ui/sonner";
import VerifyBanner from "@/components/VerifyBanner";
import AddToHomeScreenPrompt from "@/components/AddToHomeScreenPrompt";
import SplashScreen from "@/components/SplashScreen";
import NotFound from "@/pages/NotFound";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Seamless sessions: on 401, silently refresh the access token and retry once
axios.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const url = String(original?.url || "");
    if (
      error.response?.status === 401 &&
      original && !original._retry &&
      !url.includes("/auth/refresh") && !url.includes("/auth/login") && !url.includes("/auth/register")
    ) {
      original._retry = true;
      try {
        await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        return axios(original);
      } catch {
        // refresh failed — fall through to original 401
      }
    }
    return Promise.reject(error);
  }
);

// ── Main Landing Page ───────────────────────────────────────────────────────
function HomePage() {
  const [user, setUser] = useState(null); // null = loading, false = not logged in, object = logged in
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState("register");
  const [storyId, setStoryId] = useState(null);
  const [storyTitle, setStoryTitle] = useState(null);

  const openAuth = useCallback((mode = "register") => {
    setAuthMode(mode);
    setShowAuth(true);
  }, []);

  // Check auth on mount
  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((res) => setUser(res.data))
      .catch(() => setUser(false));
  }, []);

  // Scroll to hash target (e.g. /#pricing from other pages)
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.slice(1);
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 500);
    }
  }, []);

  // Lenis smooth scroll
  useEffect(() => {
    let lenis;
    import("lenis")
      .then(({ default: Lenis }) => {
        lenis = new Lenis({ duration: 1.2, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)) });
        window.__lenis = lenis;
        const raf = (time) => { lenis.raf(time); requestAnimationFrame(raf); };
        requestAnimationFrame(raf);
      })
      .catch(() => {}); // graceful fallback if lenis not available
    return () => { lenis?.destroy?.(); if (window.__lenis === lenis) window.__lenis = null; };
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    setUser(false);
  }, []);

  const handleAuth = useCallback((userData) => {
    setUser(userData);
  }, []);

  const refreshUser = useCallback(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true }).then((res) => setUser(res.data)).catch(() => {});
  }, []);

  return (
    <div className="relative min-h-screen bg-[#0B091A] overflow-x-clip">
      <StarCanvas />

      {/* All content sits above the canvas */}
      <div className="relative z-10">
        <NavHeader
          user={user}
          onAuthClick={() => openAuth("register")}
          onLoginClick={() => openAuth("login")}
          onLogout={handleLogout}
        />

        <VerifyBanner user={user} className="!mt-24" />

        <main>
          <HeroSection onCTA={() => document.getElementById("generator")?.scrollIntoView({ behavior: "smooth" })} />
          <EditorialMarquee />
          <StoryGenerator
            user={user}
            onAuthRequired={() => openAuth("register")}
            onStoryGenerated={(id, title) => { setStoryId(id); setStoryTitle(title); }}
          />
          <VoicePlayer
            storyId={storyId}
            storyTitle={storyTitle}
            user={user}
            onAuthRequired={() => openAuth("register")}
          />
          <ManifestoSection />
          <PricingSection
            user={user}
            onAuthRequired={() => openAuth("register")}
            onSubscribed={refreshUser}
          />
          <TestimonialsSection />
        </main>

        <Footer />
      </div>

      {/* Auth Modal */}
      <AnimatePresence>
        <AuthModal
          open={showAuth}
          initialMode={authMode}
          onClose={() => setShowAuth(false)}
          onAuth={handleAuth}
        />
      </AnimatePresence>
    </div>
  );
}

// ── Google Auth Callback ────────────────────────────────────────────────────
// Detects `#session_id=...` returned by https://auth.emergentagent.com, exchanges it
// for our own app JWT cookies, then hard-reloads to "/" so HomePage's normal
// /api/auth/me check picks up the new session. Processes silently, no loading UI.
function GoogleAuthCallback() {
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const params = new URLSearchParams(location.hash.replace(/^#/, ""));
    const sessionId = params.get("session_id");
    if (!sessionId) {
      window.location.replace("/");
      return;
    }
    axios
      .post(`${API}/auth/google-session`, { session_id: sessionId }, { withCredentials: true })
      .finally(() => window.location.replace("/"));
  }, [location.hash]);

  return null;
}

// ── App Routes (checks for Google auth callback before normal routing) ─────
function AppRoutes() {
  const location = useLocation();
  // Detect synchronously during render (not in an effect) to avoid race conditions
  if (location.hash?.includes("session_id=")) {
    return <GoogleAuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/my-stories" element={<MyStories />} />
      <Route path="/story/:storyId" element={<StoryReader />} />
      <Route path="/shared/:token" element={<SharedStory />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/playlist" element={<PlaylistPlayer />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

// ── App Root ────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
      <AddToHomeScreenPrompt />
      <SplashScreen />
      <Toaster position="top-center" richColors />
    </div>
  );
}
